import React, { useEffect, useRef, useState } from 'react';
import PdaPage from '../components/PdaPage';
import { getOffcutByCode, OffcutTraceItem, OffcutTraceResult } from '../services/api';
import { usePersistentFocus } from '../hooks/usePersistentFocus';
import { navigateTo } from '../utils/navigation';
import { detectScanCodeKind, normalizeScannedCode } from '../utils/pdaCodes';
import { getPdaErrorMessage } from '../utils/pdaErrors';
import { notifyError, notifySuccess } from '../utils/pdaFeedback';
import { formatDateTime, formatWeight } from '../utils/formatters';

interface TraceRenderBoundaryProps {
  children: React.ReactNode;
  resetKey: string;
}

interface TraceRenderBoundaryState {
  errorMessage: string;
}

function resolveTimelineOperator(item: OffcutTraceItem) {
  const fallbackId = item.op_by != null ? `操作人 #${item.op_by}` : '';
  return item.operator_name || item.op_by_name || fallbackId || '系统';
}

function toDisplayText(value: any, fallback = ''): string {
  if (value == null) {
    return fallback;
  }

  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }

  if (Array.isArray(value)) {
    return value.map((item) => toDisplayText(item)).join(' / ');
  }

  if (typeof value === 'object') {
    if (typeof value.label === 'string') {
      return value.label;
    }
    if (typeof value.name === 'string') {
      return value.name;
    }
    if (typeof value.code === 'string') {
      return value.code;
    }
    if (typeof value.value === 'string' || typeof value.value === 'number') {
      return String(value.value);
    }

    try {
      return JSON.stringify(value);
    } catch (error) {
      return fallback;
    }
  }

  return fallback;
}

function pickPath(source: any, path: string[]) {
  let current = source;

  for (let index = 0; index < path.length; index += 1) {
    if (current == null || typeof current !== 'object') {
      return undefined;
    }
    current = current[path[index]];
  }

  return current;
}

function pickFirst(source: any, paths: string[][]) {
  for (let index = 0; index < paths.length; index += 1) {
    const value = pickPath(source, paths[index]);
    if (value != null && value !== '') {
      return value;
    }
  }

  return undefined;
}

function normalizeLocationText(value: any): string {
  if (value == null) {
    return '';
  }

  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }

  if (typeof value === 'object') {
    return toDisplayText(
      value.loc_code || value.code || value.name || value.label || value.description,
      '',
    );
  }

  return '';
}

function normalizeWeightValue(value: any): number | undefined {
  if (value == null || value === '') {
    return undefined;
  }

  const normalized = Number(value);
  return isNaN(normalized) ? undefined : normalized;
}

function normalizeOperatorText(value: any): string {
  if (value == null) {
    return '';
  }

  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }

  if (typeof value === 'object') {
    return toDisplayText(
      value.full_name
      || value.username
      || value.emp_no
      || value.name
      || value.label
      || value.nickname,
      '',
    );
  }

  return '';
}

function resolveTraceBadge(status: string) {
  if (status === '在库') {
    return 'status-chip status-chip--green';
  }
  if (status === '已出库') {
    return 'status-chip status-chip--orange';
  }
  return 'status-chip status-chip--gray';
}

function resolveOpTypeBadge(opType: string) {
  if (opType === 'INBOUND') {
    return 'status-chip status-chip--green';
  }
  if (opType === 'TRANSFER') {
    return 'status-chip status-chip--blue';
  }
  if (opType === 'OUTBOUND') {
    return 'status-chip status-chip--orange';
  }
  return 'status-chip status-chip--gray';
}

function normalizeTimelineItem(item: any): OffcutTraceItem {
  const source = item && typeof item === 'object' ? item : {};

  return {
    from_loc: normalizeLocationText(
      pickFirst(source, [
        ['from_loc'],
        ['from_location'],
        ['fromLoc'],
      ]),
    ),
    op_at: toDisplayText(
      pickFirst(source, [
        ['op_at'],
        ['created_at'],
        ['operate_at'],
        ['timestamp'],
      ]),
      '',
    ),
    op_by: pickFirst(source, [
      ['op_by'],
      ['operator_id'],
      ['user_id'],
      ['created_by'],
    ]),
    op_by_name: normalizeOperatorText(
      pickFirst(source, [
        ['op_by_name'],
        ['operator_name'],
        ['created_by_name'],
        ['creator_name'],
        ['operator', 'name'],
        ['operator', 'full_name'],
        ['operator', 'username'],
        ['op_by', 'name'],
        ['op_by', 'full_name'],
        ['op_by', 'username'],
        ['creator', 'name'],
        ['creator', 'full_name'],
        ['creator', 'username'],
        ['user', 'name'],
        ['user', 'full_name'],
        ['user', 'username'],
      ]),
    ),
    op_detail: toDisplayText(
      pickFirst(source, [
        ['op_detail'],
        ['detail'],
        ['note'],
      ]),
      '',
    ),
    op_type: toDisplayText(
      pickFirst(source, [
        ['op_type'],
        ['type'],
        ['action'],
      ]),
      '',
    ),
    operator_name: normalizeOperatorText(
      pickFirst(source, [
        ['operator_name'],
        ['created_by_name'],
        ['creator_name'],
        ['operator', 'name'],
        ['operator', 'full_name'],
        ['operator', 'username'],
        ['op_by', 'name'],
        ['op_by', 'full_name'],
        ['op_by', 'username'],
        ['creator', 'name'],
        ['creator', 'full_name'],
        ['creator', 'username'],
        ['user', 'name'],
        ['user', 'full_name'],
        ['user', 'username'],
      ]),
    ),
    to_loc: normalizeLocationText(
      pickFirst(source, [
        ['to_loc'],
        ['to_location'],
        ['toLoc'],
      ]),
    ),
    weight: normalizeWeightValue(
      pickFirst(source, [
        ['weight'],
        ['stock_weight'],
        ['current_weight'],
      ]),
    ),
  };
}

function normalizeTraceRecordPayload(response: any, fallbackCode: string): OffcutTraceResult {
  const payload = response?.data?.data ?? response?.data ?? response ?? {};
  const timelineSource = pickFirst(payload, [
    ['timeline'],
    ['stock_logs'],
    ['logs'],
    ['records'],
  ]);
  const timeline = Array.isArray(timelineSource)
    ? timelineSource
      .filter((item: any) => item != null)
      .map(normalizeTimelineItem)
    : [];

  return {
    current_location: normalizeLocationText(
      pickFirst(payload, [
        ['current_location'],
        ['current_loc'],
        ['loc_code'],
        ['location'],
      ]),
    ),
    inbound_at: toDisplayText(
      pickFirst(payload, [
        ['inbound_at'],
        ['created_at'],
      ]),
      '',
    ),
    inbound_weight: normalizeWeightValue(
      pickFirst(payload, [
        ['inbound_weight'],
        ['weight_in'],
      ]),
    ),
    ocsf: toDisplayText(
      pickFirst(payload, [
        ['ocsf'],
        ['bid'],
        ['code'],
        ['offcut_code'],
        ['offcut', 'ocsf'],
        ['offcut', 'bid'],
      ]),
      fallbackCode,
    ),
    offcut_stock_id: payload.offcut_stock_id || 0,
    outbound_weight: normalizeWeightValue(
      pickFirst(payload, [
        ['outbound_weight'],
        ['weight_out'],
      ]),
    ),
    sku: toDisplayText(
      pickFirst(payload, [
        ['sku'],
        ['raw_material_sku'],
        ['material_sku'],
      ]),
      '',
    ),
    status: toDisplayText(
      pickFirst(payload, [
        ['status'],
        ['status_name'],
        ['status_label'],
      ]),
      '未知状态',
    ),
    stock_weight: normalizeWeightValue(
      pickFirst(payload, [
        ['stock_weight'],
        ['current_weight'],
        ['inventory_weight'],
        ['weight'],
      ]),
    ),
    timeline,
  };
}

class TraceRenderBoundary extends React.Component<TraceRenderBoundaryProps, TraceRenderBoundaryState> {
  constructor(props: TraceRenderBoundaryProps) {
    super(props);
    this.state = {
      errorMessage: '',
    };
  }

  static getDerivedStateFromError(error: Error): TraceRenderBoundaryState {
    return {
      errorMessage: error && error.message ? error.message : '追溯页渲染失败',
    };
  }

  componentDidUpdate(prevProps: TraceRenderBoundaryProps) {
    if (prevProps.resetKey !== this.props.resetKey && this.state.errorMessage) {
      this.setState({
        errorMessage: '',
      });
    }
  }

  render() {
    if (this.state.errorMessage) {
      return (
        <section className="panel empty-state">
          {`追溯页渲染失败：${this.state.errorMessage}`}
        </section>
      );
    }

    return this.props.children;
  }
}

function OffcutTrace() {
  const inputRef = useRef<HTMLInputElement>(null);
  const scanTimerRef = useRef<number>(0);
  const [scanValue, setScanValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [record, setRecord] = useState<OffcutTraceResult | null>(null);

  usePersistentFocus(inputRef, !loading, loading ? 'loading' : 'ready');
  useEffect(() => () => window.clearTimeout(scanTimerRef.current), []);

  function resetTrace() {
    clearScanInput();
    setRecord(null);
  }

  function clearScanInput() {
    window.clearTimeout(scanTimerRef.current);
    setScanValue('');
    if (inputRef.current) {
      inputRef.current.value = '';
    }
  }

  function scheduleLookup(rawCode: string) {
    if (loading) {
      return;
    }

    setScanValue(rawCode);
    window.clearTimeout(scanTimerRef.current);
    scanTimerRef.current = window.setTimeout(() => {
      void handleLookup(inputRef.current?.value ?? rawCode);
    }, 120);
  }

  async function handleLookup(rawCode?: string) {
    const code = normalizeScannedCode(rawCode ?? inputRef.current?.value ?? scanValue);
    const kind = detectScanCodeKind(code);

    if (!code || loading) {
      return;
    }

    if (kind !== 'offcut') {
      if (kind === 'equipment') {
        notifyError('设备扫码功能尚未开放');
      } else if (kind === 'location') {
        notifyError('追溯页只接受料头码');
      } else {
        notifyError('请扫描料头 QR');
      }
      clearScanInput();
      return;
    }

    setLoading(true);
    try {
      const response = await getOffcutByCode(code);
      const nextRecord = normalizeTraceRecordPayload(response, code);
      setRecord(nextRecord);
      notifySuccess(`已查询 ${nextRecord.ocsf || code}`);
    } catch (error) {
      notifyError(getPdaErrorMessage(error, '追溯查询失败'));
      setRecord(null);
    } finally {
      setLoading(false);
      clearScanInput();
    }
  }

  return (
    <PdaPage
      actionLabel="清空"
      onAction={resetTrace}
      onBack={() => navigateTo('/home')}
      subtitle="扫料头码，查看当前位置和最近时间线"
      title="料头追溯"
    >
      <section className="panel">
        <div className="panel-title">扫料头 QR</div>
        <div className="panel-hint">只接受 LT- 或 OCSF 开头的料头码。</div>
        <div className="scan-field" style={{ marginTop: 16 }}>
          <label className="scan-label" htmlFor="trace-offcut-code">
            料头码
          </label>
          <input
            autoComplete="off"
            autoFocus
            autoCapitalize="off"
            autoCorrect="off"
            className="scan-input"
            id="trace-offcut-code"
            inputMode="none"
            onInput={(event) => scheduleLookup(event.currentTarget.value)}
            onKeyUp={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                void handleLookup(event.currentTarget.value);
              }
            }}
            placeholder="扫描后按回车..."
            ref={inputRef}
            spellCheck={false}
          />
        </div>
        <div className="button-row" style={{ marginTop: 16 }}>
          <button className="primary-btn" disabled={loading || !scanValue.trim()} onClick={() => void handleLookup()} type="button">
            {loading ? '查询中...' : '查询'}
          </button>
        </div>
      </section>

      <TraceRenderBoundary resetKey={record ? record.ocsf : 'empty'}>
        {record ? (
          <>
            <section className="panel">
              <div className="panel-title">当前位置</div>
              <div className="panel-hint" style={{ marginTop: 12 }}>
                <span className={resolveTraceBadge(record.status)}>{record.status}</span>
              </div>
              <div className="data-grid" style={{ marginTop: 16 }}>
                <div className="metric-card">
                  <div className="metric-label">料头码</div>
                  <div className="metric-value">{record.ocsf}</div>
                </div>
                <div className="metric-card">
                  <div className="metric-label">当前库位</div>
                  <div className="metric-value">{record.current_location || '未上架'}</div>
                </div>
                <div className="metric-card">
                  <div className="metric-label">库存重量</div>
                  <div className="metric-value">{formatWeight(record.stock_weight)}kg</div>
                </div>
                <div className="metric-card">
                  <div className="metric-label">SKU</div>
                  <div className="metric-value">{record.sku || '--'}</div>
                </div>
              </div>
            </section>

            <section>
              <div className="panel-title" style={{ padding: '0 4px' }}>
                最近时间线
              </div>
              <div className="timeline-list" style={{ marginTop: 12 }}>
                {record.timeline.length === 0 ? (
                  <div className="panel empty-state">还没有移动记录。</div>
                ) : (
                  record.timeline.slice(0, 10).map((item, index) => (
                    <div className="timeline-item" key={`${item.op_at}-${item.op_type}-${index}`}>
                      <div className="timeline-item__top">
                        <span className={resolveOpTypeBadge(item.op_type)}>{item.op_type}</span>
                        <span className="summary-subvalue">{formatDateTime(item.op_at)}</span>
                      </div>
                      <div className="timeline-item__path">
                        {(item.from_loc || '无库位')}
                        {' → '}
                        {(item.to_loc || '无库位')}
                      </div>
                      <div className="timeline-item__detail">
                        重量：{formatWeight(item.weight)}kg
                        <br />
                        操作人：{resolveTimelineOperator(item)}
                      </div>
                      {item.op_detail ? (
                        <div className="timeline-item__meta">备注：{item.op_detail}</div>
                      ) : null}
                    </div>
                  ))
                )}
              </div>
            </section>
          </>
        ) : (
          <section className="panel empty-state">
            扫描料头码后，这里会显示当前位置、状态和最近 10 条移动时间线。
          </section>
        )}
      </TraceRenderBoundary>
    </PdaPage>
  );
}

export default OffcutTrace;
